<script setup>
import EmailingAutomationEmailExclusionTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationEmailExclusionTable.vue';
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import DisplayTemplateHelperAction from '@js/components/actions/DisplayTemplateHelperAction.vue';
import DisplayVariablesAction from '@js/components/actions/DisplayVariablesAction.vue';
import ManageMediasAction from '@js/components/actions/ManageMediasAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import HtmlPreview from '@js/components/contents/HtmlPreview.vue';
import CodeField from '@js/components/fields/CodeField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFieldGroup from '@js/components/forms/FormFieldGroup.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import SortableList from '@js/components/lists/SortableList.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, h, onMounted, reactive, ref } from 'vue';
import { array, boolean, number, object, string } from 'yup';

const {
    tenant,
    emailingAutomation,
    emailingAutomationTypes,
    emailingAutomationCustomerTypes,
    orderStatuses,
    promoPriceRules,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailingAutomation: {
        type: Object,
        required: false,
    },
    emailingAutomationTypes: {
        type: Array,
        required: true,
    },
    emailingAutomationCustomerTypes: {
        type: Array,
        required: true,
    },
    orderStatuses: {
        type: Array,
        required: true,
    },
    promoPriceRules: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const { route } = useRoute();

const cartAmountOperatorOptions = [
    { value: '>=', label: '≥' },
    { value: '>', label: '>' },
    { value: '<=', label: '≤' },
    { value: '<', label: '<' },
];

function cartAmountOperatorLabel(value) {
    return (
        cartAmountOperatorOptions.find((option) => option.value === value)
            ?.label ?? ''
    );
}

let stepUidCounter = 0;

function nextStepUid() {
    return `${++stepUidCounter}`;
}

function newStep() {
    return {
        id: null,
        isActive: true,
        subject: '',
        template: '',
        data: {
            delayAfterOpeningDays: 0,
            delayAfterOpeningHours: 0,
            delayAfterOpeningMinutes: 0,
            delayAfterNotOpeningDays: 0,
            delayAfterNotOpeningHours: 0,
            delayAfterNotOpeningMinutes: 0,
        },
        _uid: nextStepUid(),
    };
}

const form = reactive({
    name: emailingAutomation ? emailingAutomation.name : '',
    description: emailingAutomation ? emailingAutomation.description : '',
    type: emailingAutomation
        ? emailingAutomationTypes.find(
              (type) => type.value === emailingAutomation.type.value,
          )
        : emailingAutomationTypes[0],
    isActive: emailingAutomation ? emailingAutomation.isActive : false,
    isBeingTested: emailingAutomation
        ? emailingAutomation.isBeingTested
        : false,
    subject: emailingAutomation ? emailingAutomation.subject : '',
    senderName: emailingAutomation ? emailingAutomation.senderName : '',
    senderEmail: emailingAutomation ? emailingAutomation.senderEmail : '',
    replyToName: emailingAutomation ? emailingAutomation.replyToName : '',
    replyToEmail: emailingAutomation ? emailingAutomation.replyToEmail : '',
    recipientName: emailingAutomation ? emailingAutomation.recipientName : '',
    recipientEmail: emailingAutomation ? emailingAutomation.recipientEmail : '',
    sendBccEmail: emailingAutomation ? emailingAutomation.sendBccEmail : false,
    template: emailingAutomation ? emailingAutomation.template : '',
    data: {
        customerType: 'all',
        cartAmountOperator: '>=',
        cartAmountValue: null,
        ...(emailingAutomation ? emailingAutomation.data || {} : {}),
    },
    invoicedAt: emailingAutomation ? emailingAutomation.invoicedAt : null,
    promoPriceRuleId: emailingAutomation
        ? (emailingAutomation.promoPriceRuleId ?? null)
        : null,
    steps: (emailingAutomation?.steps ?? []).map((step) => ({
        ...step,
        _uid: nextStepUid(),
    })),
});

function promoPriceRuleLabel(id) {
    const promoPriceRule = promoPriceRules.find((rule) => rule.id === id);

    if (!promoPriceRule) {
        return 'Aucun';
    }

    return (
        promoPriceRule.name +
        ' (' +
        promoPriceRule.discountType.label.toLowerCase() +
        ' ' +
        promoPriceRule.discountValue +
        ')'
    );
}

const customerType = computed({
    get() {
        return (
            emailingAutomationCustomerTypes.find(
                (type) => type.value === form.data.customerType,
            ) || emailingAutomationCustomerTypes[0]
        );
    },
    set(type) {
        form.data.customerType = type.value;
    },
});

const isPreviewing = ref(false);

const isSubmitting = ref(false);

const selectedTab = ref();

const previewHtml = ref('');

const loadingPreview = ref(true);

const validationSchema = computed(() => {
    let schema = {
        name: string().required('Le nom est requis'),
        description: string().nullable(),
        type: object().required('Le type est requis'),
        isActive: boolean().required('Le statut actif est requis'),
        isBeingTested: boolean().required('Le statut de test est requis'),
        subject: string().required('Le sujet est requis'),
        senderName: string().required("Le nom de l'expéditeur est requis"),
        senderEmail: string()
            .required("L'email de l'expéditeur est requis")
            .email(
                "L'email de l'expéditeur doit être une adresse email valide",
            ),
        replyToName: string().nullable(),
        replyToEmail: string()
            .nullable()
            .email("L'email de réponse doit être une adresse email valide"),
        recipientName: string().nullable(),
        recipientEmail: string()
            .nullable()
            .email(
                "L'email du destinataire doit être une adresse email valide",
            ),
        sendBccEmail: boolean().required(
            "Le statut d'envoi de l'email en copie cachée est requis",
        ),
        template: string().required('Le template est requis'),
        promoPriceRuleId: number().nullable(),
        invoicedAt: string().nullable(),
    };

    if (form.type.value === 'abandoned_cart') {
        schema = {
            ...schema,
            data: object().shape({
                startDate: string().required('La date de début est requise'),
                days: number().required(
                    "Le nombre de jours après lequel envoyer l'email est requis",
                ),
                hours: number().required(
                    "Le nombre d'heures après lequel envoyer l'email est requis",
                ),
                minutes: number().required(
                    "Le nombre de minutes après lequel envoyer l'email est requis",
                ),
                customerType: object().required(
                    'Le type de client ciblé est requis',
                ),
                cartAmountOperator: string().nullable(),
                cartAmountValue: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le montant du panier doit être positif')
                    .nullable(),
            }),
        };
    }

    if (form.type.value === 'welcome') {
        schema = {
            ...schema,
            data: object().shape({
                startDate: string().required('La date de début est requise'),
                days: number().required(
                    "Le nombre de jours après lequel envoyer l'email est requis",
                ),
                hours: number().required(
                    "Le nombre d'heures après lequel envoyer l'email est requis",
                ),
                minutes: number().required(
                    "Le nombre de minutes après lequel envoyer l'email est requis",
                ),
                customerType: object().required(
                    'Le type de client ciblé est requis',
                ),
            }),
        };
    }

    if (form.type.value === 'order_canceled') {
        schema = {
            ...schema,
            data: object().shape({
                orderStatuses: array()
                    .of(string())
                    .min(
                        1,
                        'Au moins un statut de commande doit être sélectionné',
                    )
                    .required('Les statuts de commande sont requis'),
                startDate: string().required('La date de début est requise'),
                days: number().required(
                    "Le nombre de jours après lequel envoyer l'email est requis",
                ),
                hours: number().required(
                    "Le nombre d'heures après lequel envoyer l'email est requis",
                ),
                minutes: number().required(
                    "Le nombre de minutes après lequel envoyer l'email est requis",
                ),
            }),
        };
    }

    if (form.type.value === 'request_for_opinion') {
        schema = {
            ...schema,
            data: object().shape({
                orderStatuses: array()
                    .of(string())
                    .min(
                        1,
                        'Au moins un statut de commande doit être sélectionné',
                    )
                    .required('Les statuts de commande sont requis'),
                startDate: string().required('La date de début est requise'),
                days: number().required(
                    "Le nombre de jours après lequel envoyer l'email est requis",
                ),
                hours: number().required(
                    "Le nombre d'heures après lequel envoyer l'email est requis",
                ),
                minutes: number().required(
                    "Le nombre de minutes après lequel envoyer l'email est requis",
                ),
                maximumNumberOfOrders: number().required(
                    'Le nombre maximum de commandes est requis',
                ),
            }),
        };
    }

    if (form.type.value === 'best_customer') {
        schema = {
            ...schema,
            data: object().shape({
                overTheLastXDays: number().required(
                    'Le nombre de jours à prendre en compte est requis',
                ),
                overTheLastXHours: number().required(
                    "Le nombre d'heures à prendre en compte est requis",
                ),
                overTheLastXMinutes: number().required(
                    'Le nombre de minutes à prendre en compte est requis',
                ),
                sentAfterXDays: number().required(
                    'Le nombre de jours après lequel envoyer le premier email est requis',
                ),
                sentAfterXHours: number().required(
                    "Le nombre d'heures après lequel envoyer le premier email est requis",
                ),
                sentAfterXMinutes: number().required(
                    'Le nombre de minutes après lequel envoyer le premier email est requis',
                ),
                minimumNumberOfOrders: number().required(
                    'Le nombre minimum de commandes est requis',
                ),
                minimumPurchaseAmount: number().required(
                    "Le montant minimum d'achats est requis",
                ),
            }),
        };
    }

    if (form.type.value === 'reactivation') {
        schema = {
            ...schema,
            data: object().shape({
                startDate: string().required('La date de début est requise'),
                overTheLastXDays: number().required(
                    'Le nombre de jours à prendre en compte est requis',
                ),
                overTheLastXHours: number().required(
                    "Le nombre d'heures à prendre en compte est requis",
                ),
                overTheLastXMinutes: number().required(
                    'Le nombre de minutes à prendre en compte est requis',
                ),
                maxOverTheLastXDays: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le nombre de jours doit être positif')
                    .nullable(),
                maxOverTheLastXHours: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, "Le nombre d'heures doit être positif")
                    .nullable(),
                maxOverTheLastXMinutes: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le nombre de minutes doit être positif')
                    .nullable(),
                minAccountAgeXDays: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le nombre de jours doit être positif')
                    .nullable(),
                minAccountAgeXHours: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, "Le nombre d'heures doit être positif")
                    .nullable(),
                minAccountAgeXMinutes: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le nombre de minutes doit être positif')
                    .nullable(),
                maxAccountAgeXDays: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le nombre de jours doit être positif')
                    .nullable(),
                maxAccountAgeXHours: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, "Le nombre d'heures doit être positif")
                    .nullable(),
                maxAccountAgeXMinutes: number()
                    .transform((value, originalValue) =>
                        originalValue === '' || originalValue === null
                            ? null
                            : value,
                    )
                    .min(0, 'Le nombre de minutes doit être positif')
                    .nullable(),
                sentAfterXDays: number().required(
                    'Le nombre de jours après lequel envoyer le premier email est requis',
                ),
                sentAfterXHours: number().required(
                    "Le nombre d'heures après lequel envoyer le premier email est requis",
                ),
                sentAfterXMinutes: number().required(
                    'Le nombre de minutes après lequel envoyer le premier email est requis',
                ),
                customerType: object().required(
                    'Le type de client ciblé est requis',
                ),
            }),
        };
    }

    if (['abandoned_cart', 'welcome'].includes(form.type.value)) {
        form.steps.forEach((step) => {
            schema[`stepSubject_${step._uid}`] = string().required(
                'Le sujet est requis',
            );
            schema[`stepTemplate_${step._uid}`] = string().required(
                'Le template est requis',
            );
            schema[`stepDelayAfterOpeningDays_${step._uid}`] =
                number().required(
                    'Le nombre de jours après ouverture est requis',
                );
            schema[`stepDelayAfterOpeningHours_${step._uid}`] =
                number().required(
                    "Le nombre d'heures après ouverture est requis",
                );
            schema[`stepDelayAfterOpeningMinutes_${step._uid}`] =
                number().required(
                    'Le nombre de minutes après ouverture est requis',
                );
            schema[`stepDelayAfterNotOpeningDays_${step._uid}`] =
                number().required(
                    'Le nombre de jours après non-ouverture est requis',
                );
            schema[`stepDelayAfterNotOpeningHours_${step._uid}`] =
                number().required(
                    "Le nombre d'heures après non-ouverture est requis",
                );
            schema[`stepDelayAfterNotOpeningMinutes_${step._uid}`] =
                number().required(
                    'Le nombre de minutes après non-ouverture est requis',
                );
        });
    }

    return object(schema);
});

function preview() {
    isPreviewing.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postPreviewEmailingAutomationTemplate',
                {
                    tenant: tenant.id,
                },
            ),
            {
                type: form.type.value,
                template: form.template,
            },
        )
        .then((response) => {
            previewHtml.value = response.data.html;
        })
        .finally(() => {
            loadingPreview.value = false;
        })
        .catch(() => {
            previewHtml.value =
                "Une erreur est survenue lors de la génération de l'aperçu.";
        });
}

const stepPreviewsHtml = reactive({});

const stepPreviewsLoading = reactive({});

function previewStep(step) {
    stepPreviewsLoading[step._uid] = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postPreviewEmailingAutomationTemplate',
                {
                    tenant: tenant.id,
                },
            ),
            {
                type: form.type.value,
                template: step.template,
            },
        )
        .then((response) => {
            stepPreviewsHtml[step._uid] = response.data.html;
        })
        .catch(() => {
            stepPreviewsHtml[step._uid] =
                "Une erreur est survenue lors de la génération de l'aperçu.";
        })
        .finally(() => {
            stepPreviewsLoading[step._uid] = false;
        });
}

function submit() {
    isSubmitting.value = true;

    let data = form.data;

    axios
        .post(
            route('front.projectManagement.modules.postEmailingAutomation', {
                tenant: tenant.id,
                ...(emailingAutomation
                    ? { emailingAutomation: emailingAutomation.id }
                    : {}),
            }),
            {
                name: form.name,
                description: form.description,
                type: form.type.value,
                isActive: form.isActive,
                isBeingTested: form.isBeingTested,
                subject: form.subject,
                senderName: form.senderName,
                senderEmail: form.senderEmail,
                replyToName: form.replyToName,
                replyToEmail: form.replyToEmail,
                recipientName: form.recipientName,
                recipientEmail: form.recipientEmail,
                sendBccEmail: form.sendBccEmail,
                template: form.template,
                data: data,
                promoPriceRuleId: form.promoPriceRuleId,
                invoicedAt: form.invoicedAt,
                steps: form.steps.map((step) => ({
                    id: step.id ?? null,
                    isActive: step.isActive,
                    subject: step.subject,
                    template: step.template,
                    data: {
                        delayAfterOpeningDays: step.data.delayAfterOpeningDays,
                        delayAfterOpeningHours:
                            step.data.delayAfterOpeningHours,
                        delayAfterOpeningMinutes:
                            step.data.delayAfterOpeningMinutes,
                        delayAfterNotOpeningDays:
                            step.data.delayAfterNotOpeningDays,
                        delayAfterNotOpeningHours:
                            step.data.delayAfterNotOpeningHours,
                        delayAfterNotOpeningMinutes:
                            step.data.delayAfterNotOpeningMinutes,
                    },
                })),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.emailingAutomations', {
                    tenant: tenant.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.emailingAutomations', {
                    tenant: tenant.id,
                }),
            );
        });
}

onMounted(() => {
    preview();

    form.steps.forEach((step) => previewStep(step));
});
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <TabLayout
            v-model:selected-tab="selectedTab"
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                scenario: {
                    title: 'Scénario',
                    contentSlotName: 'scenario',
                    forceMount: true,
                },
                ...(emailingAutomation
                    ? {
                          emailExclusions: {
                              title: 'Exclusions d\'emails',
                              content: h(
                                  EmailingAutomationEmailExclusionTable,
                                  {
                                      tenant: tenant,
                                      emailingAutomation: emailingAutomation,
                                  },
                              ),
                              forceMount: true,
                          },
                      }
                    : {}),
            }"
            :unmount-on-hide="false"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <InputField
                            v-model="form.name"
                            label="Nom"
                            field-name="name"
                            field-id="name"
                            type="text"
                            required
                        />

                        <EditorField
                            v-model="form.description"
                            label="Description"
                            field-name="description"
                            field-id="description"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <SwitchField
                            v-model="form.isActive"
                            label="Est actif"
                            field-name="isActive"
                            field-id="isActive"
                        />

                        <SwitchField
                            v-model="form.isBeingTested"
                            label="Est en test"
                            field-name="isBeingTested"
                            field-id="isBeingTested"
                        />

                        <InputField
                            v-model="form.invoicedAt"
                            label="Date de facturation"
                            field-name="invoicedAt"
                            field-id="invoicedAt"
                            type="date"
                        />
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.senderName"
                                label="Nom de l'expéditeur"
                                field-name="senderName"
                                field-id="senderName"
                                type="text"
                                required
                            />

                            <InputField
                                v-model="form.senderEmail"
                                label="Email de l'expéditeur"
                                field-name="senderEmail"
                                field-id="senderEmail"
                                type="email"
                                required
                            />

                            <InputField
                                v-model="form.replyToName"
                                label="Nom de la réponse"
                                field-name="replyToName"
                                field-id="replyToName"
                                type="text"
                            />

                            <InputField
                                v-model="form.replyToEmail"
                                label="Email de la réponse"
                                field-name="replyToEmail"
                                field-id="replyToEmail"
                                type="email"
                            />

                            <InputField
                                v-model="form.recipientName"
                                label="Nom du destinataire"
                                field-name="recipientName"
                                field-id="recipientName"
                                type="text"
                            />

                            <InputField
                                v-model="form.recipientEmail"
                                label="Email du destinataire"
                                field-name="recipientEmail"
                                field-id="recipientEmail"
                                type="email"
                            />
                        </FormGrid>

                        <SwitchField
                            v-model="form.sendBccEmail"
                            :label="
                                'Envoyer un email en copie cachée' +
                                ' (relances@apikom.fr)'
                            "
                            field-name="sendBccEmail"
                            field-id="sendBccEmail"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #scenario>
                <FormColumn>
                    <FormContent>
                        <FormTitle>Scénario</FormTitle>

                        <FormGrid>
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :legend="
                                    emailingAutomation
                                        ? 'Le type de scénario ne peut pas être modifié après la création.'
                                        : null
                                "
                                :options="emailingAutomationTypes"
                                by="value"
                                :disabled="!!emailingAutomation"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Email initial</FormTitle>

                        <InputField
                            v-model="form.subject"
                            label="Sujet"
                            field-name="subject"
                            field-id="subject"
                            type="text"
                            required
                        />

                        <FormGrid :columns="2">
                            <CodeField
                                v-model="form.template"
                                label="Template"
                                field-name="template"
                                field-id="template"
                                required
                                :disabled="loadingPreview"
                            />

                            <HtmlPreview
                                :html="previewHtml"
                                :loading="loadingPreview"
                            >
                                <template #actions>
                                    <DisplayTemplateHelperAction>
                                        <template #trigger>
                                            <ActionButton
                                                type="secondary"
                                                size="small"
                                            >
                                                Aide
                                            </ActionButton>
                                        </template>
                                    </DisplayTemplateHelperAction>

                                    <DisplayVariablesAction
                                        title="Variables"
                                        :variables="
                                            form.type.variableInformation
                                        "
                                    >
                                        <template #trigger>
                                            <ActionButton
                                                type="secondary"
                                                size="small"
                                            >
                                                Variables
                                            </ActionButton>
                                        </template>
                                    </DisplayVariablesAction>

                                    <ManageMediasAction
                                        :path="
                                            'tenant/' +
                                            tenant.id +
                                            '/emailing-automations/media'
                                        "
                                    >
                                        <template #trigger>
                                            <ActionButton
                                                type="secondary"
                                                size="small"
                                            >
                                                Médias
                                            </ActionButton>
                                        </template>
                                    </ManageMediasAction>

                                    <ActionButton
                                        :callback="() => preview()"
                                        size="small"
                                        :loading="loadingPreview"
                                    >
                                        Prévisualiser
                                    </ActionButton>
                                </template>
                            </HtmlPreview>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Code promo</FormTitle>

                        <FormGrid>
                            <ListboxField
                                v-model="form.promoPriceRuleId"
                                field-id="promoPriceRuleId"
                                field-name="promoPriceRuleId"
                                label="Règle de prix promo"
                                legend="Attribue un code promo unique à chaque destinataire lors de l'envoi. Insérez la variable $promo_code (entre doubles accolades) dans le sujet ou le template."
                                :options="promoPriceRules"
                                option-key="id"
                                has-empty-option
                            >
                                <template #buttonContent>
                                    {{
                                        promoPriceRuleLabel(
                                            form.promoPriceRuleId,
                                        )
                                    }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }} ({{
                                        option.discountType.label.toLowerCase()
                                    }}
                                    {{ option.discountValue }})
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <div :key="form.type.value" class="contents">
                        <FormColumn
                            v-if="form.type.value === 'abandoned_cart'"
                            key="scenario-abandoned_cart"
                        >
                            <FormContent>
                                <FormTitle>Délai d'envoi</FormTitle>

                                <FormFieldGroup
                                    legend="Délai entre le déclenchement du scénario et l'envoi de l'email initial (jours + heures + minutes cumulés)."
                                >
                                    <InputField
                                        v-model="form.data.days"
                                        label="Envoyé après X jours"
                                        field-name="data.days"
                                        field-id="data.days"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.hours"
                                        label="Envoyé après X heures"
                                        field-name="data.hours"
                                        field-id="data.hours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.minutes"
                                        label="Envoyé après X minutes"
                                        field-name="data.minutes"
                                        field-id="data.minutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle
                                    >Conditions de déclenchement</FormTitle
                                >

                                <FormGrid>
                                    <InputField
                                        v-model="form.data.startDate"
                                        label="Date de début"
                                        legend="Ne cible que les paniers abandonnés à partir de cette date."
                                        field-name="data.startDate"
                                        field-id="data.startDate"
                                        type="date"
                                        required
                                    />
                                </FormGrid>

                                <FormFieldGroup
                                    legend="N'envoie l'email que si le montant du panier respecte cette condition. Laisser le montant vide pour ne pas filtrer sur le montant."
                                >
                                    <ListboxField
                                        v-model="form.data.cartAmountOperator"
                                        field-id="data.cartAmountOperator"
                                        field-name="data.cartAmountOperator"
                                        label="Opérateur"
                                        :options="cartAmountOperatorOptions"
                                        option-key="value"
                                        size="small"
                                    >
                                        <template #buttonContent>
                                            {{
                                                cartAmountOperatorLabel(
                                                    form.data
                                                        .cartAmountOperator,
                                                )
                                            }}
                                        </template>

                                        <template #optionContent="{ option }">
                                            {{ option.label }}
                                        </template>
                                    </ListboxField>

                                    <InputField
                                        v-model="form.data.cartAmountValue"
                                        field-id="data.cartAmountValue"
                                        field-name="data.cartAmountValue"
                                        label="Montant du panier"
                                        legend="Laisser vide pour désactiver ce filtre"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        size="small"
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle>Ciblage clients</FormTitle>

                                <FormGrid>
                                    <ListboxField
                                        v-model="customerType"
                                        field-id="data.customerType"
                                        field-name="data.customerType"
                                        label="Clients ciblés"
                                        legend="Restreint l'envoi selon l'historique d'achat : un prospect n'a jamais commandé, un client a passé au moins une commande."
                                        :options="
                                            emailingAutomationCustomerTypes
                                        "
                                        by="value"
                                        required
                                    >
                                        <template #buttonContent>
                                            {{ customerType.label }}
                                        </template>

                                        <template #optionContent="{ option }">
                                            {{ option.label }}
                                        </template>
                                    </ListboxField>
                                </FormGrid>
                            </FormContent>

                            <FormContent>
                                <FormTitle>Relances</FormTitle>

                                <SortableList
                                    key="steps-abandoned_cart"
                                    v-model="form.steps"
                                    :sortable="false"
                                    :numbered="true"
                                    add-label="Ajouter une relance"
                                    empty-text="Aucune relance configurée. Le panier ne recevra que l'email initial."
                                    :new-item="
                                        form.steps.length < 2 ? newStep : null
                                    "
                                >
                                    <template #default="{ item, index }">
                                        <div class="flex flex-col gap-4">
                                            <SwitchField
                                                v-model="item.isActive"
                                                label="Relance active"
                                                :field-name="`stepActive_${item._uid}`"
                                                :field-id="`stepActive_${item._uid}`"
                                            />

                                            <InputField
                                                v-model="item.subject"
                                                :label="`Sujet de la relance ${index + 1}`"
                                                :field-name="`stepSubject_${item._uid}`"
                                                :field-id="`stepSubject_${item._uid}`"
                                                type="text"
                                                required
                                            />

                                            <FormGrid :columns="2">
                                                <CodeField
                                                    v-model="item.template"
                                                    label="Template"
                                                    :field-name="`stepTemplate_${item._uid}`"
                                                    :field-id="`stepTemplate_${item._uid}`"
                                                    required
                                                    :disabled="
                                                        stepPreviewsLoading[
                                                            item._uid
                                                        ] ?? false
                                                    "
                                                />

                                                <HtmlPreview
                                                    :html="
                                                        stepPreviewsHtml[
                                                            item._uid
                                                        ] ?? ''
                                                    "
                                                    :loading="
                                                        stepPreviewsLoading[
                                                            item._uid
                                                        ] ?? false
                                                    "
                                                >
                                                    <template #actions>
                                                        <DisplayTemplateHelperAction>
                                                            <template #trigger>
                                                                <ActionButton
                                                                    type="secondary"
                                                                    size="small"
                                                                >
                                                                    Aide
                                                                </ActionButton>
                                                            </template>
                                                        </DisplayTemplateHelperAction>

                                                        <DisplayVariablesAction
                                                            title="Variables"
                                                            :variables="
                                                                form.type
                                                                    .variableInformation
                                                            "
                                                        >
                                                            <template #trigger>
                                                                <ActionButton
                                                                    type="secondary"
                                                                    size="small"
                                                                >
                                                                    Variables
                                                                </ActionButton>
                                                            </template>
                                                        </DisplayVariablesAction>

                                                        <ManageMediasAction
                                                            :path="
                                                                'tenant/' +
                                                                tenant.id +
                                                                '/emailing-automations/media'
                                                            "
                                                        >
                                                            <template #trigger>
                                                                <ActionButton
                                                                    type="secondary"
                                                                    size="small"
                                                                >
                                                                    Médias
                                                                </ActionButton>
                                                            </template>
                                                        </ManageMediasAction>

                                                        <ActionButton
                                                            :callback="
                                                                () =>
                                                                    previewStep(
                                                                        item,
                                                                    )
                                                            "
                                                            size="small"
                                                            :loading="
                                                                stepPreviewsLoading[
                                                                    item._uid
                                                                ] ?? false
                                                            "
                                                        >
                                                            Prévisualiser
                                                        </ActionButton>
                                                    </template>
                                                </HtmlPreview>
                                            </FormGrid>

                                            <FormFieldGroup
                                                legend="Délai si l'email précédent a été ouvert."
                                            >
                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterOpeningDays
                                                    "
                                                    label="Après ouverture : X jours"
                                                    :field-name="`stepDelayAfterOpeningDays_${item._uid}`"
                                                    :field-id="`stepDelayAfterOpeningDays_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterOpeningHours
                                                    "
                                                    label="Après ouverture : X heures"
                                                    :field-name="`stepDelayAfterOpeningHours_${item._uid}`"
                                                    :field-id="`stepDelayAfterOpeningHours_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterOpeningMinutes
                                                    "
                                                    label="Après ouverture : X minutes"
                                                    :field-name="`stepDelayAfterOpeningMinutes_${item._uid}`"
                                                    :field-id="`stepDelayAfterOpeningMinutes_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />
                                            </FormFieldGroup>

                                            <FormFieldGroup
                                                legend="Délai si l'email précédent n'a pas été ouvert."
                                            >
                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterNotOpeningDays
                                                    "
                                                    label="Après non-ouverture : X jours"
                                                    :field-name="`stepDelayAfterNotOpeningDays_${item._uid}`"
                                                    :field-id="`stepDelayAfterNotOpeningDays_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterNotOpeningHours
                                                    "
                                                    label="Après non-ouverture : X heures"
                                                    :field-name="`stepDelayAfterNotOpeningHours_${item._uid}`"
                                                    :field-id="`stepDelayAfterNotOpeningHours_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterNotOpeningMinutes
                                                    "
                                                    label="Après non-ouverture : X minutes"
                                                    :field-name="`stepDelayAfterNotOpeningMinutes_${item._uid}`"
                                                    :field-id="`stepDelayAfterNotOpeningMinutes_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />
                                            </FormFieldGroup>
                                        </div>
                                    </template>
                                </SortableList>
                            </FormContent>
                        </FormColumn>

                        <FormColumn
                            v-else-if="form.type.value === 'order_canceled'"
                            key="scenario-order_canceled"
                        >
                            <FormContent>
                                <FormTitle>Délai d'envoi</FormTitle>

                                <FormFieldGroup
                                    legend="Délai entre le déclenchement du scénario et l'envoi de l'email initial (jours + heures + minutes cumulés)."
                                >
                                    <InputField
                                        v-model="form.data.days"
                                        label="Envoyé après X jours"
                                        field-name="data.days"
                                        field-id="data.days"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.hours"
                                        label="Envoyé après X heures"
                                        field-name="data.hours"
                                        field-id="data.hours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.minutes"
                                        label="Envoyé après X minutes"
                                        field-name="data.minutes"
                                        field-id="data.minutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle
                                    >Conditions de déclenchement</FormTitle
                                >

                                <FormGrid>
                                    <InputField
                                        v-model="form.data.startDate"
                                        label="Date de début"
                                        legend="Ne cible que les commandes annulées à partir de cette date."
                                        field-name="data.startDate"
                                        field-id="data.startDate"
                                        type="date"
                                        required
                                    />
                                </FormGrid>

                                <FormGrid>
                                    <ListboxField
                                        v-model="form.data.orderStatuses"
                                        field-id="data.orderStatuses"
                                        field-name="data.orderStatuses"
                                        label="Statuts de commande"
                                        legend="Seules les commandes ayant l'un de ces statuts déclenchent l'envoi."
                                        :options="orderStatuses"
                                        required
                                        multiple
                                    >
                                        <template #buttonContent>
                                            <template
                                                v-if="
                                                    form.data.orderStatuses &&
                                                    form.data.orderStatuses
                                                        .length > 0
                                                "
                                            >
                                                {{
                                                    form.data.orderStatuses.join(
                                                        ', ',
                                                    )
                                                }}
                                            </template>

                                            <template v-else> &nbsp; </template>
                                        </template>

                                        <template #optionContent="{ option }">
                                            {{ option }}
                                        </template>
                                    </ListboxField>
                                </FormGrid>
                            </FormContent>
                        </FormColumn>

                        <FormColumn
                            v-else-if="
                                form.type.value === 'request_for_opinion'
                            "
                            key="scenario-request_for_opinion"
                        >
                            <FormContent>
                                <FormTitle>Délai d'envoi</FormTitle>

                                <FormFieldGroup
                                    legend="Délai entre le déclenchement du scénario et l'envoi de l'email initial (jours + heures + minutes cumulés)."
                                >
                                    <InputField
                                        v-model="form.data.days"
                                        label="Envoyé après X jours"
                                        field-name="data.days"
                                        field-id="data.days"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.hours"
                                        label="Envoyé après X heures"
                                        field-name="data.hours"
                                        field-id="data.hours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.minutes"
                                        label="Envoyé après X minutes"
                                        field-name="data.minutes"
                                        field-id="data.minutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle
                                    >Conditions de déclenchement</FormTitle
                                >

                                <FormGrid>
                                    <InputField
                                        v-model="form.data.startDate"
                                        label="Date de début"
                                        legend="Ne cible que les commandes livrées à partir de cette date."
                                        field-name="data.startDate"
                                        field-id="data.startDate"
                                        type="date"
                                        required
                                    />
                                </FormGrid>

                                <FormGrid :columns="2">
                                    <ListboxField
                                        v-model="form.data.orderStatuses"
                                        field-id="data.orderStatuses"
                                        field-name="data.orderStatuses"
                                        label="Statuts de commande"
                                        legend="Seules les commandes ayant l'un de ces statuts déclenchent l'envoi."
                                        :options="orderStatuses"
                                        required
                                        multiple
                                    >
                                        <template #buttonContent>
                                            <template
                                                v-if="
                                                    form.data.orderStatuses &&
                                                    form.data.orderStatuses
                                                        .length > 0
                                                "
                                            >
                                                {{
                                                    form.data.orderStatuses.join(
                                                        ', ',
                                                    )
                                                }}
                                            </template>

                                            <template v-else> &nbsp; </template>
                                        </template>

                                        <template #optionContent="{ option }">
                                            {{ option }}
                                        </template>
                                    </ListboxField>

                                    <InputField
                                        v-model="
                                            form.data.maximumNumberOfOrders
                                        "
                                        label="Nombre maximum de commandes"
                                        legend="Ne cible que les clients ayant passé au plus ce nombre de commandes (pour viser les nouveaux clients)."
                                        field-name="data.maximumNumberOfOrders"
                                        field-id="data.maximumNumberOfOrders"
                                        type="number"
                                        step="1"
                                        min="0"
                                        required
                                    />
                                </FormGrid>
                            </FormContent>
                        </FormColumn>

                        <FormColumn
                            v-else-if="form.type.value === 'best_customer'"
                            key="scenario-best_customer"
                        >
                            <FormContent>
                                <FormTitle>Délai d'envoi</FormTitle>

                                <FormFieldGroup
                                    legend="Délai après la dernière commande de référence avant l'envoi du premier email."
                                >
                                    <InputField
                                        v-model="form.data.sentAfterXDays"
                                        label="Envoyé après X jours"
                                        field-name="data.sentAfterXDays"
                                        field-id="data.sentAfterXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.sentAfterXHours"
                                        label="Envoyé après X heures"
                                        field-name="data.sentAfterXHours"
                                        field-id="data.sentAfterXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.sentAfterXMinutes"
                                        label="Envoyé après X minutes"
                                        field-name="data.sentAfterXMinutes"
                                        field-id="data.sentAfterXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle
                                    >Conditions de déclenchement</FormTitle
                                >

                                <FormFieldGroup
                                    legend="Fenêtre d'analyse : seules les commandes passées sur cette période sont comptabilisées."
                                >
                                    <InputField
                                        v-model="form.data.overTheLastXDays"
                                        label="Sur les X derniers jours"
                                        field-name="data.overTheLastXDays"
                                        field-id="data.overTheLastXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.overTheLastXHours"
                                        label="Sur les X dernières heures"
                                        field-name="data.overTheLastXHours"
                                        field-id="data.overTheLastXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.overTheLastXMinutes"
                                        label="Sur les X dernières minutes"
                                        field-name="data.overTheLastXMinutes"
                                        field-id="data.overTheLastXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>

                                <FormGrid :columns="2">
                                    <InputField
                                        v-model="
                                            form.data.minimumNumberOfOrders
                                        "
                                        label="Nombre minimum de commandes"
                                        legend="Nombre minimum de commandes sur la période pour être considéré comme meilleur client."
                                        field-name="data.minimumNumberOfOrders"
                                        field-id="data.minimumNumberOfOrders"
                                        type="number"
                                        step="1"
                                        min="0"
                                        required
                                    />

                                    <InputField
                                        v-model="
                                            form.data.minimumPurchaseAmount
                                        "
                                        label="Montant minimum d'achats"
                                        legend="Montant total minimum d'achats sur la période pour être éligible."
                                        field-name="data.minimumPurchaseAmount"
                                        field-id="data.minimumPurchaseAmount"
                                        type="number"
                                        step="1"
                                        min="0"
                                        required
                                    />
                                </FormGrid>
                            </FormContent>
                        </FormColumn>

                        <FormColumn
                            v-else-if="form.type.value === 'reactivation'"
                            key="scenario-reactivation"
                        >
                            <FormContent>
                                <FormTitle>Délai d'envoi</FormTitle>

                                <FormFieldGroup
                                    legend="Délai avant l'envoi de l'email de réactivation, une fois le client considéré inactif."
                                >
                                    <InputField
                                        v-model="form.data.sentAfterXDays"
                                        label="Envoyé après X jours"
                                        field-name="data.sentAfterXDays"
                                        field-id="data.sentAfterXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.sentAfterXHours"
                                        label="Envoyé après X heures"
                                        field-name="data.sentAfterXHours"
                                        field-id="data.sentAfterXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.sentAfterXMinutes"
                                        label="Envoyé après X minutes"
                                        field-name="data.sentAfterXMinutes"
                                        field-id="data.sentAfterXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle
                                    >Conditions de déclenchement</FormTitle
                                >

                                <FormGrid>
                                    <InputField
                                        v-model="form.data.startDate"
                                        label="Date de début"
                                        legend="Ne cible que les clients dont le compte a été créé à partir de cette date."
                                        field-name="data.startDate"
                                        field-id="data.startDate"
                                        type="date"
                                        required
                                    />
                                </FormGrid>

                                <FormFieldGroup
                                    legend="Clients inactifs sur cette période (aucune commande passée, aucun emailing automatisé envoyé)."
                                >
                                    <InputField
                                        v-model="form.data.overTheLastXDays"
                                        label="Sur les X derniers jours"
                                        field-name="data.overTheLastXDays"
                                        field-id="data.overTheLastXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.overTheLastXHours"
                                        label="Sur les X dernières heures"
                                        field-name="data.overTheLastXHours"
                                        field-id="data.overTheLastXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.overTheLastXMinutes"
                                        label="Sur les X dernières minutes"
                                        field-name="data.overTheLastXMinutes"
                                        field-id="data.overTheLastXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle>
                                    Ancienneté du compte client (optionnel)
                                </FormTitle>

                                <FormFieldGroup
                                    legend="Ne cible que les comptes créés il y a au moins ce délai (le compte doit avoir un certain âge). Laisser vide pour ne pas filtrer."
                                >
                                    <InputField
                                        v-model="form.data.minAccountAgeXDays"
                                        label="Il y a plus de X jours"
                                        field-name="data.minAccountAgeXDays"
                                        field-id="data.minAccountAgeXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />

                                    <InputField
                                        v-model="form.data.minAccountAgeXHours"
                                        label="Il y a plus de X heures"
                                        field-name="data.minAccountAgeXHours"
                                        field-id="data.minAccountAgeXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />

                                    <InputField
                                        v-model="
                                            form.data.minAccountAgeXMinutes
                                        "
                                        label="Il y a plus de X minutes"
                                        field-name="data.minAccountAgeXMinutes"
                                        field-id="data.minAccountAgeXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />
                                </FormFieldGroup>

                                <FormFieldGroup
                                    legend="Ne cible que les comptes créés il y a au plus ce délai (comptes récents). Laisser vide pour ne pas filtrer."
                                >
                                    <InputField
                                        v-model="form.data.maxAccountAgeXDays"
                                        label="Il y a moins de X jours"
                                        field-name="data.maxAccountAgeXDays"
                                        field-id="data.maxAccountAgeXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />

                                    <InputField
                                        v-model="form.data.maxAccountAgeXHours"
                                        label="Il y a moins de X heures"
                                        field-name="data.maxAccountAgeXHours"
                                        field-id="data.maxAccountAgeXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />

                                    <InputField
                                        v-model="
                                            form.data.maxAccountAgeXMinutes
                                        "
                                        label="Il y a moins de X minutes"
                                        field-name="data.maxAccountAgeXMinutes"
                                        field-id="data.maxAccountAgeXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle>
                                    Borne haute d'inactivité (optionnel)
                                </FormTitle>

                                <FormFieldGroup
                                    legend="Ne cible que les clients ayant passé une commande dans cette fenêtre plus large mais aucune sur la fenêtre ci-dessus. Laisser vide pour ne pas limiter l'ancienneté (ex. scénario « 24 mois et plus »)."
                                >
                                    <InputField
                                        v-model="form.data.maxOverTheLastXDays"
                                        label="Jusqu'à X jours"
                                        field-name="data.maxOverTheLastXDays"
                                        field-id="data.maxOverTheLastXDays"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />

                                    <InputField
                                        v-model="form.data.maxOverTheLastXHours"
                                        label="Jusqu'à X heures"
                                        field-name="data.maxOverTheLastXHours"
                                        field-id="data.maxOverTheLastXHours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />

                                    <InputField
                                        v-model="
                                            form.data.maxOverTheLastXMinutes
                                        "
                                        label="Jusqu'à X minutes"
                                        field-name="data.maxOverTheLastXMinutes"
                                        field-id="data.maxOverTheLastXMinutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle>Ciblage clients</FormTitle>

                                <FormGrid>
                                    <ListboxField
                                        v-model="customerType"
                                        field-id="data.customerType"
                                        field-name="data.customerType"
                                        label="Clients ciblés"
                                        legend="Restreint l'envoi selon l'historique d'achat : un prospect n'a jamais commandé, un client a passé au moins une commande."
                                        :options="
                                            emailingAutomationCustomerTypes
                                        "
                                        by="value"
                                        required
                                    >
                                        <template #buttonContent>
                                            {{ customerType.label }}
                                        </template>

                                        <template #optionContent="{ option }">
                                            {{ option.label }}
                                        </template>
                                    </ListboxField>
                                </FormGrid>
                            </FormContent>
                        </FormColumn>

                        <FormColumn
                            v-else-if="form.type.value === 'welcome'"
                            key="scenario-welcome"
                        >
                            <FormContent>
                                <FormTitle>Délai d'envoi</FormTitle>

                                <FormFieldGroup
                                    legend="Délai entre l'inscription et l'envoi de l'email de bienvenue (jours + heures + minutes cumulés)."
                                >
                                    <InputField
                                        v-model="form.data.days"
                                        label="Envoyé après X jours"
                                        field-name="data.days"
                                        field-id="data.days"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.hours"
                                        label="Envoyé après X heures"
                                        field-name="data.hours"
                                        field-id="data.hours"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />

                                    <InputField
                                        v-model="form.data.minutes"
                                        label="Envoyé après X minutes"
                                        field-name="data.minutes"
                                        field-id="data.minutes"
                                        type="number"
                                        step="1"
                                        min="0"
                                        size="small"
                                        required
                                    />
                                </FormFieldGroup>
                            </FormContent>

                            <FormContent>
                                <FormTitle
                                    >Conditions de déclenchement</FormTitle
                                >

                                <FormGrid>
                                    <InputField
                                        v-model="form.data.startDate"
                                        label="Date de début"
                                        legend="Ne cible que les inscriptions reçues à partir de cette date."
                                        field-name="data.startDate"
                                        field-id="data.startDate"
                                        type="date"
                                        required
                                    />
                                </FormGrid>
                            </FormContent>

                            <FormContent>
                                <FormTitle>Ciblage clients</FormTitle>

                                <FormGrid>
                                    <ListboxField
                                        v-model="customerType"
                                        field-id="data.customerType"
                                        field-name="data.customerType"
                                        label="Clients ciblés"
                                        legend="Restreint l'envoi selon l'historique d'achat : un prospect n'a jamais commandé, un client a passé au moins une commande (rapprochement sur l'email de l'inscrit)."
                                        :options="
                                            emailingAutomationCustomerTypes
                                        "
                                        by="value"
                                        required
                                    >
                                        <template #buttonContent>
                                            {{ customerType.label }}
                                        </template>

                                        <template #optionContent="{ option }">
                                            {{ option.label }}
                                        </template>
                                    </ListboxField>
                                </FormGrid>
                            </FormContent>

                            <FormContent>
                                <FormTitle>Relances</FormTitle>

                                <SortableList
                                    key="steps-welcome"
                                    v-model="form.steps"
                                    :sortable="false"
                                    :numbered="true"
                                    add-label="Ajouter une relance"
                                    empty-text="Aucune relance configurée. L'inscrit ne recevra que l'email de bienvenue."
                                    :new-item="
                                        form.steps.length < 1 ? newStep : null
                                    "
                                >
                                    <template #default="{ item, index }">
                                        <div class="flex flex-col gap-4">
                                            <SwitchField
                                                v-model="item.isActive"
                                                label="Relance active"
                                                :field-name="`stepActive_${item._uid}`"
                                                :field-id="`stepActive_${item._uid}`"
                                            />

                                            <InputField
                                                v-model="item.subject"
                                                :label="`Sujet de la relance ${index + 1}`"
                                                :field-name="`stepSubject_${item._uid}`"
                                                :field-id="`stepSubject_${item._uid}`"
                                                type="text"
                                                required
                                            />

                                            <FormGrid :columns="2">
                                                <CodeField
                                                    v-model="item.template"
                                                    label="Template"
                                                    :field-name="`stepTemplate_${item._uid}`"
                                                    :field-id="`stepTemplate_${item._uid}`"
                                                    required
                                                    :disabled="
                                                        stepPreviewsLoading[
                                                            item._uid
                                                        ] ?? false
                                                    "
                                                />

                                                <HtmlPreview
                                                    :html="
                                                        stepPreviewsHtml[
                                                            item._uid
                                                        ] ?? ''
                                                    "
                                                    :loading="
                                                        stepPreviewsLoading[
                                                            item._uid
                                                        ] ?? false
                                                    "
                                                >
                                                    <template #actions>
                                                        <DisplayTemplateHelperAction>
                                                            <template #trigger>
                                                                <ActionButton
                                                                    type="secondary"
                                                                    size="small"
                                                                >
                                                                    Aide
                                                                </ActionButton>
                                                            </template>
                                                        </DisplayTemplateHelperAction>

                                                        <DisplayVariablesAction
                                                            title="Variables"
                                                            :variables="
                                                                form.type
                                                                    .variableInformation
                                                            "
                                                        >
                                                            <template #trigger>
                                                                <ActionButton
                                                                    type="secondary"
                                                                    size="small"
                                                                >
                                                                    Variables
                                                                </ActionButton>
                                                            </template>
                                                        </DisplayVariablesAction>

                                                        <ManageMediasAction
                                                            :path="
                                                                'tenant/' +
                                                                tenant.id +
                                                                '/emailing-automations/media'
                                                            "
                                                        >
                                                            <template #trigger>
                                                                <ActionButton
                                                                    type="secondary"
                                                                    size="small"
                                                                >
                                                                    Médias
                                                                </ActionButton>
                                                            </template>
                                                        </ManageMediasAction>

                                                        <ActionButton
                                                            :callback="
                                                                () =>
                                                                    previewStep(
                                                                        item,
                                                                    )
                                                            "
                                                            size="small"
                                                            :loading="
                                                                stepPreviewsLoading[
                                                                    item._uid
                                                                ] ?? false
                                                            "
                                                        >
                                                            Prévisualiser
                                                        </ActionButton>
                                                    </template>
                                                </HtmlPreview>
                                            </FormGrid>

                                            <FormFieldGroup
                                                legend="Délai si l'email précédent a été ouvert."
                                            >
                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterOpeningDays
                                                    "
                                                    label="Après ouverture : X jours"
                                                    :field-name="`stepDelayAfterOpeningDays_${item._uid}`"
                                                    :field-id="`stepDelayAfterOpeningDays_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterOpeningHours
                                                    "
                                                    label="Après ouverture : X heures"
                                                    :field-name="`stepDelayAfterOpeningHours_${item._uid}`"
                                                    :field-id="`stepDelayAfterOpeningHours_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterOpeningMinutes
                                                    "
                                                    label="Après ouverture : X minutes"
                                                    :field-name="`stepDelayAfterOpeningMinutes_${item._uid}`"
                                                    :field-id="`stepDelayAfterOpeningMinutes_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />
                                            </FormFieldGroup>

                                            <FormFieldGroup
                                                legend="Délai si l'email précédent n'a pas été ouvert."
                                            >
                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterNotOpeningDays
                                                    "
                                                    label="Après non-ouverture : X jours"
                                                    :field-name="`stepDelayAfterNotOpeningDays_${item._uid}`"
                                                    :field-id="`stepDelayAfterNotOpeningDays_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterNotOpeningHours
                                                    "
                                                    label="Après non-ouverture : X heures"
                                                    :field-name="`stepDelayAfterNotOpeningHours_${item._uid}`"
                                                    :field-id="`stepDelayAfterNotOpeningHours_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />

                                                <InputField
                                                    v-model="
                                                        item.data
                                                            .delayAfterNotOpeningMinutes
                                                    "
                                                    label="Après non-ouverture : X minutes"
                                                    :field-name="`stepDelayAfterNotOpeningMinutes_${item._uid}`"
                                                    :field-id="`stepDelayAfterNotOpeningMinutes_${item._uid}`"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    size="small"
                                                    required
                                                />
                                            </FormFieldGroup>
                                        </div>
                                    </template>
                                </SortableList>
                            </FormContent>
                        </FormColumn>
                    </div>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter
            v-if="selectedTab !== 'emailExclusions'"
            :is-submitting="isSubmitting"
            :errors="errors"
        >
            <template #actions>
                <DeleteAction
                    v-if="emailingAutomation"
                    :url="
                        route(
                            'front.projectManagement.modules.deleteEmailingAutomation',
                            {
                                tenant: tenant.id,
                                emailingAutomation: emailingAutomation.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.modules.emailingAutomations',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
